const PROMPTAI_API_BASE = "https://www.nohaya.com/promptai/api";
export const PROMPTAI_PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.crede.prompt_generator";

export type PromptCategory = {
  id: number;
  name: string;
  slug: string;
  description: string;
  icon_name: string;
  color_index: number;
  sort_order: number;
  prompt_count: number;
};

export type PromptSummary = {
  id: number;
  title: string;
  description: string;
  hint: string;
  image_url: string;
  view_count: number;
  favorite_count: number;
  trending_score: number;
  trending_period: string | null;
  platforms: string[];
  created_at: string;
  category_id: number;
  category_name: string;
  category_color_index: number;
};

export type PromptDetail = PromptSummary & { content: string };

export async function getCategories(): Promise<PromptCategory[]> {
  try {
    const res = await fetch(`${PROMPTAI_API_BASE}/categories/list.php`, {
      signal: AbortSignal.timeout(8000),
      next: { tags: ["prompts"] },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data.success ? data.categories : [];
  } catch {
    return [];
  }
}

export async function getAllPrompts(): Promise<PromptSummary[]> {
  const all: PromptSummary[] = [];
  let page = 1;
  const limit = 50;
  for (;;) {
    try {
      const res = await fetch(`${PROMPTAI_API_BASE}/prompts/list.php?page=${page}&limit=${limit}`, {
        signal: AbortSignal.timeout(8000),
        next: { tags: ["prompts"] },
      });
      if (!res.ok) break;
      const data = await res.json();
      if (!data.success) break;
      all.push(...(data.prompts ?? []));
      if (!data.has_more) break;
      page += 1;
    } catch {
      break;
    }
  }
  return all;
}

export async function getPromptDetail(id: number): Promise<PromptDetail | null> {
  try {
    const res = await fetch(`${PROMPTAI_API_BASE}/prompts/detail.php?id=${id}`, {
      signal: AbortSignal.timeout(8000),
      next: { tags: ["prompts", `prompt-${id}`] },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.id ? data : null;
  } catch {
    return null;
  }
}

export function slugifyTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export function promptSlug(p: { id: number; title: string }): string {
  return `${slugifyTitle(p.title)}-${p.id}`;
}

export function parsePromptIdFromSlug(slug: string): number | null {
  const match = slug.match(/-(\d+)$/);
  return match ? Number(match[1]) : null;
}

// Clean, ID-free slug map ported from PHP's nohaya_build_prompt_slug_map()
// (php/prompts-pages/lib/prompts_data.php) -- this is the scheme the
// currently-live, already-indexed /prompts/{slug} URLs use, distinct from
// (and newer than) promptSlug()/parsePromptIdFromSlug() above, which still
// reflect the pre-PHP-migration `title-id` format. NOT yet wired into the
// Next.js fallback pages (src/app/prompts/[slug]/page.tsx still uses the
// old parsePromptIdFromSlug regex) -- that page needs updating to use this
// map before it can serve as the Phase 4 Render Service's source of truth
// for prompts. Kept here rather than duplicated because the admin publish
// flow (src/lib/promptsAdminDb.ts) needs the exact same mapping to know
// what slug to enqueue.
export function buildPromptSlugMap(prompts: { id: number; title: string }[]): Map<number, string> {
  const sorted = [...prompts].sort((a, b) => a.id - b.id);
  const map = new Map<number, string>();
  const seen = new Set<string>();
  for (const p of sorted) {
    const base = slugifyTitle(p.title);
    let slug = base;
    let n = 2;
    while (seen.has(slug)) {
      slug = `${base}-${n}`;
      n += 1;
    }
    seen.add(slug);
    map.set(p.id, slug);
  }
  return map;
}

export function promptIdFromCleanSlug(slug: string, prompts: { id: number; title: string }[]): number | null {
  const map = buildPromptSlugMap(prompts);
  for (const [id, s] of map) {
    if (s === slug) return id;
  }
  return null;
}

export type PromptSlugResolution =
  | { kind: "clean"; id: number }
  | { kind: "legacy"; id: number; canonicalSlug: string }
  | { kind: "not-found" };

// Single entry point for resolving an incoming /prompts/{slug} URL against
// both the current clean-slug scheme and the pre-migration `title-id`
// format, so old indexed/bookmarked links still resolve (via a 301 to the
// canonical slug) instead of 404ing -- matches the redirect behavior PHP's
// prompt.php already implements for the same legacy format.
export function resolvePromptSlug(slug: string, prompts: { id: number; title: string }[]): PromptSlugResolution {
  const cleanId = promptIdFromCleanSlug(slug, prompts);
  if (cleanId !== null) return { kind: "clean", id: cleanId };

  const legacyId = parsePromptIdFromSlug(slug);
  if (legacyId !== null && prompts.some((p) => p.id === legacyId)) {
    const canonicalSlug = buildPromptSlugMap(prompts).get(legacyId);
    if (canonicalSlug) return { kind: "legacy", id: legacyId, canonicalSlug };
  }

  return { kind: "not-found" };
}

const CATEGORY_COLOR_PALETTE = [
  "bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400",
  "bg-pink-50 text-pink-600 dark:bg-pink-500/10 dark:text-pink-400",
  "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400",
  "bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400",
  "bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400",
  "bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400",
  "bg-cyan-50 text-cyan-600 dark:bg-cyan-500/10 dark:text-cyan-400",
  "bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400",
  "bg-green-50 text-green-600 dark:bg-green-500/10 dark:text-green-400",
  "bg-fuchsia-50 text-fuchsia-600 dark:bg-fuchsia-500/10 dark:text-fuchsia-400",
];

export function categoryColorClass(colorIndex: number): string {
  return CATEGORY_COLOR_PALETTE[colorIndex % CATEGORY_COLOR_PALETTE.length];
}

const CATEGORY_GRADIENT_PALETTE = [
  "from-blue-500 to-cyan-500",
  "from-pink-500 to-rose-500",
  "from-emerald-500 to-teal-500",
  "from-violet-500 to-fuchsia-500",
  "from-orange-500 to-amber-500",
  "from-amber-500 to-yellow-500",
  "from-cyan-500 to-blue-500",
  "from-rose-500 to-pink-500",
  "from-green-500 to-emerald-500",
  "from-fuchsia-500 to-purple-500",
];

export function categoryGradientClass(colorIndex: number): string {
  return CATEGORY_GRADIENT_PALETTE[colorIndex % CATEGORY_GRADIENT_PALETTE.length];
}
